// src/components/Admin/AdminEditSellerInvoice.jsx
import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  TextField,
  Button,
  Autocomplete,
  Paper,
  Typography,
  Divider,
  IconButton,
  Grid,
  Box,
  MenuItem,
} from "@mui/material";
import { Add, Remove } from "@mui/icons-material";
import { useParams, useNavigate } from "react-router-dom";
import { server } from "../../server";

const AdminEditSellerInvoice = () => {
  const { id } = useParams(); // Invoice ID from URL
  const navigate = useNavigate();
  // Form state variables
  const [selectedSeller, setSelectedSeller] = useState(null);
  const [sellerOptions, setSellerOptions] = useState([]); // For seller search
  const [items, setItems] = useState([]);
  const [shippingCharges, setShippingCharges] = useState(0);
  const [totalValueWithoutGst, setTotalValueWithoutGst] = useState("0.00");
  const [totalGstValue, setTotalGstValue] = useState("0.00");
  const [totalInvoiceValue, setTotalInvoiceValue] = useState("0.00");
  const [purchaseOrderNumber, setPurchaseOrderNumber] = useState("");
  // Default payment method is now "Cash Paid"
  const [paymentMethod, setPaymentMethod] = useState("Cash Paid");
  const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().split("T")[0]);
  const [invoiceNumber, setInvoiceNumber] = useState("");

  // Fetch invoice details when component mounts
  useEffect(() => {
    fetchInvoiceDetails();
  }, [id]);

  const fetchInvoiceDetails = async () => {
    try {
      // Ensure your backend GET route populates sellerId with all needed fields
      const response = await axios.get(`${server}/sellerInvoice/get-seller-invoice/${id}`, {
        withCredentials: true,
      });
      if (response.data.success) {
        const invoice = response.data.sellerInvoice;
        // Set selectedSeller (assumed to be populated via populate)
        setSelectedSeller(invoice.sellerId);
        // Map invoice items to the form structure
        const mappedItems = invoice.items.map((item) => ({
          name: item.name,
          description: item.description,
          qty: item.qty,
          pricePerQty: item.pricePerQty || "Nos",
          pricePerQtyWithoutGST: item.discountPriceWithCommission,
          gstPercentage: item.gstPercentage,
          hsnCode: item.hsnCode,
          totalPrice: parseFloat(item.totalPriceWithCommission),
        }));
        setItems(mappedItems);
        setShippingCharges(invoice.shippingCharges || 0);
        setPurchaseOrderNumber(invoice.purchaseOrderNumber || "");
        setPaymentMethod(invoice.paymentMethod || "Cash Paid");
        const invDate = new Date(invoice.date).toISOString().split("T")[0];
        setInvoiceDate(invDate);
        setInvoiceNumber(invoice.invoiceNumber || "");
      }
    } catch (error) {
      console.error("Error fetching invoice details:", error);
      alert("Failed to fetch invoice details.");
    }
  };

  // Recalculate totals when items or shipping charges change
  useEffect(() => {
    calculateTotals();
  }, [items, shippingCharges]);

  const calculateTotals = () => {
    let totalWithoutGst = 0;
    let totalGst = 0;
    items.forEach((item) => {
      const qty = parseFloat(item.qty) || 0;
      const pricePerQtyWithoutGST = parseFloat(item.pricePerQtyWithoutGST) || 0;
      const gstPercentage = parseFloat(item.gstPercentage) || 0;
      const priceWithoutGst = qty * pricePerQtyWithoutGST;
      const gstValue = (priceWithoutGst * gstPercentage) / 100;
      totalWithoutGst += priceWithoutGst;
      totalGst += gstValue;
    });
    const totalVal = totalWithoutGst + totalGst + parseFloat(shippingCharges || 0);
    setTotalValueWithoutGst(totalWithoutGst.toFixed(2));
    setTotalGstValue(totalGst.toFixed(2));
    setTotalInvoiceValue(totalVal.toFixed(2));
  };

  // Seller search: When the admin types into the seller field,
  // call the backend /shop/search route to get matching sellers.
  const handleSellerSearch = async (event, value) => {
    if (value && value.length >= 1) {
      try {
        const response = await axios.get(`${server}/shop/search?query=${value}`, {
          withCredentials: true,
        });
        if (response.data.success) {
          setSellerOptions(response.data.shops);
        }
      } catch (error) {
        console.error("Error fetching seller options:", error);
      }
    } else {
      setSellerOptions([]);
    }
  };

  // When a seller is selected, update the selectedSeller state.
  // The seller details will then appear in the read-only fields below.
  const handleSellerSelect = (event, value) => {
    setSelectedSeller(value);
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...items];
    updatedItems[index][field] = value;
    if (["qty", "pricePerQtyWithoutGST", "gstPercentage"].includes(field)) {
      const qty = parseFloat(updatedItems[index].qty) || 0;
      const pricePerQtyWithoutGST = parseFloat(updatedItems[index].pricePerQtyWithoutGST) || 0;
      const gstPercentage = parseFloat(updatedItems[index].gstPercentage) || 0;
      const priceWithoutGst = qty * pricePerQtyWithoutGST;
      const gstValue = (priceWithoutGst * gstPercentage) / 100;
      updatedItems[index].totalPrice = priceWithoutGst + gstValue;
    }
    setItems(updatedItems);
  };

  const handleAddItem = () => {
    setItems([
      ...items,
      {
        name: "",
        description: "",
        qty: 1,
        pricePerQty: "Nos",
        pricePerQtyWithoutGST: "",
        gstPercentage: "",
        hsnCode: "",
        totalPrice: 0,
      },
    ]);
  };

  const handleRemoveItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!selectedSeller) {
      alert("Seller is required.");
      return;
    }
    if (!purchaseOrderNumber) {
      alert("Please enter a Purchase Order Number.");
      return;
    }
    if (!invoiceDate) {
      alert("Please select an invoice date.");
      return;
    }

    // Prepare items data for update
    const itemsData = items.map((item, index) => {
      const qty = parseFloat(item.qty) || 0;
      const pricePerQtyWithoutGST = parseFloat(item.pricePerQtyWithoutGST) || 0;
      const gstPercentage = parseFloat(item.gstPercentage) || 0;
      const priceWithoutGst = qty * pricePerQtyWithoutGST;
      const gstValue = (priceWithoutGst * gstPercentage) / 100;
      const totalPrice = priceWithoutGst + gstValue;
      return {
        name: item.name,
        description: item.description,
        qty,
        gstPercentage,
        pricePerQty: item.pricePerQty,
        totalPrice: priceWithoutGst.toFixed(2),
        hsnCode: item.hsnCode,
        sno: index + 1,
        discountPriceWithCommission: pricePerQtyWithoutGST.toFixed(2),
        totalPriceWithCommission: totalPrice.toFixed(2),
      };
    });

    const totalValueWithCommission = parseFloat(totalValueWithoutGst) + parseFloat(totalGstValue);

    const sellerInvoiceData = {
      sellerId: selectedSeller._id,
      items: itemsData,
      shippingCharges: parseFloat(shippingCharges),
      totalValueWithoutGst: parseFloat(totalValueWithoutGst),
      totalGstValue: parseFloat(totalGstValue),
      totalInvoiceValue: parseFloat(totalInvoiceValue),
      totalValueWithCommission: parseFloat(totalValueWithCommission),
      purchaseOrderNumber,
      paymentMethod,
      invoiceDate,
      invoiceNumber,
    };

    try {
      const response = await axios.put(
        `${server}/sellerInvoice/edit-seller-invoice/${id}`,
        sellerInvoiceData,
        { withCredentials: true }
      );
      if (response.data.success) {
        alert("Seller Invoice updated successfully.");
        navigate("/admin-seller-invoices");
      }
    } catch (error) {
      console.error("Error updating seller invoice:", error);
      alert("Failed to update seller invoice.");
    }
  };

  return (
    <Paper sx={{ padding: 4, margin: "20px auto", maxWidth: "1200px" }}>
      <Typography variant="h4" gutterBottom>Edit Seller Invoice</Typography>
      <Divider sx={{ marginY: 2 }} />

      {/* Seller, Invoice Date, and Invoice Number */}
      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          {/* Allow changing the seller via an Autocomplete */}
          <Autocomplete
            options={sellerOptions}
            getOptionLabel={(option) => `${option.name} - ${option.email}`}
            onInputChange={handleSellerSearch}
            onChange={handleSellerSelect}
            renderInput={(params) => <TextField {...params} label="Seller" variant="outlined" />}
            fullWidth
            value={selectedSeller}
           
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            label="Invoice Date"
            type="date"
            variant="outlined"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={invoiceDate}
            onChange={(e) => setInvoiceDate(e.target.value)}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            label="Invoice Number"
            variant="outlined"
            fullWidth
            value={invoiceNumber}
            onChange={(e) => setInvoiceNumber(e.target.value)}
          />
        </Grid>
      </Grid>

      {/* Seller Details (read-only) */}
      {selectedSeller && (
        <Box sx={{ marginTop: 3 }}>
          <Typography variant="h6">Seller Details</Typography>
          <Grid container spacing={2} sx={{ marginTop: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Name"
                variant="outlined"
                fullWidth
                value={selectedSeller.name || ""}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Email"
                variant="outlined"
                fullWidth
                value={selectedSeller.email || ""}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Phone Number"
                variant="outlined"
                fullWidth
                value={selectedSeller.phoneNumber || ""}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="GST Number"
                variant="outlined"
                fullWidth
                value={selectedSeller.gstNumber || ""}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Address"
                variant="outlined"
                fullWidth
                value={selectedSeller.address || selectedSeller.address || ""}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Zip Code"
                variant="outlined"
                fullWidth
                value={selectedSeller.zipCode || ""}
                InputProps={{ readOnly: true }}
              />
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Invoice Details */}
      <Box sx={{ marginTop: 4 }}>
        <Typography variant="h6">Invoice Details</Typography>
        <Grid container spacing={4} sx={{ marginTop: 1 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Purchase Order Number"
              variant="outlined"
              fullWidth
              value={purchaseOrderNumber}
              onChange={(e) => setPurchaseOrderNumber(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              select
              label="Payment Method"
              variant="outlined"
              fullWidth
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
            >
              <MenuItem value="Razorpay">Razorpay</MenuItem>
              <MenuItem value="Cash Paid">Cash Paid</MenuItem>
              <MenuItem value="Pay on Credit">Pay on Credit</MenuItem>
            </TextField>
          </Grid>
        </Grid>
      </Box>

      {/* Items */}
      <Box sx={{ marginTop: 4 }}>
        <Typography variant="h6">Items</Typography>
        {items.map((item, index) => (
          <Paper
            key={index}
            sx={{ padding: 2, marginTop: 2, backgroundColor: "#f9f9f9" }}
            elevation={3}
          >
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Item Name"
                  variant="outlined"
                  fullWidth
                  value={item.name}
                  onChange={(e) => handleItemChange(index, "name", e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Description"
                  variant="outlined"
                  fullWidth
                  value={item.description}
                  onChange={(e) => handleItemChange(index, "description", e.target.value)}
                />
              </Grid>
              <Grid item xs={6} sm={2}>
                <TextField
                  label="Quantity"
                  variant="outlined"
                  type="number"
                  fullWidth
                  value={item.qty}
                  onChange={(e) => handleItemChange(index, "qty", e.target.value)}
                />
              </Grid>
              <Grid item xs={6} sm={2}>
                <TextField
                  label="Price / Qty (Without GST)"
                  variant="outlined"
                  type="number"
                  fullWidth
                  value={item.pricePerQtyWithoutGST}
                  onChange={(e) => handleItemChange(index, "pricePerQtyWithoutGST", e.target.value)}
                />
              </Grid>
              <Grid item xs={6} sm={2}>
                <TextField
                  select
                  label="Price per Unit"
                  variant="outlined"
                  fullWidth
                  value={item.pricePerQty}
                  onChange={(e) => handleItemChange(index, "pricePerQty", e.target.value)}
                >
                  {[
                    "Nos",
                    "Kilogram",
                    "Gram",
                    "Meter",
                    "Millimeter",
                    "Centimeter",
                    "Foot",
                    "Square foot",
                    "Litre",
                    "Millilitre",
                    "Cubic meter",
                    "Dozen",
                  ].map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={6} sm={2}>
                <TextField
                  label="GST (%)"
                  variant="outlined"
                  type="number"
                  fullWidth
                  value={item.gstPercentage}
                  onChange={(e) => handleItemChange(index, "gstPercentage", e.target.value)}
                />
              </Grid>
              <Grid item xs={6} sm={2}>
                <TextField
                  label="HSN Code"
                  variant="outlined"
                  fullWidth
                  value={item.hsnCode}
                  onChange={(e) => handleItemChange(index, "hsnCode", e.target.value)}
                />
              </Grid>
              <Grid item xs={6} sm={2}>
                <TextField
                  label="Total Price"
                  variant="outlined"
                  type="number"
                  fullWidth
                  value={parseFloat(item.totalPrice).toFixed(2)}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <IconButton color="error" onClick={() => handleRemoveItem(index)}>
                  <Remove />
                </IconButton>
                {index === items.length - 1 && (
                  <IconButton color="primary" onClick={handleAddItem}>
                    <Add />
                  </IconButton>
                )}
              </Grid>
            </Grid>
          </Paper>
        ))}
      </Box>

      {/* Shipping Charges & Totals */}
      <Box sx={{ marginTop: 4 }}>
        <Typography variant="h6">Additional Charges & Totals</Typography>
        <Grid container spacing={4} sx={{ marginTop: 1 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Additional Shipping Charges"
              variant="outlined"
              type="number"
              fullWidth
              value={shippingCharges}
              onChange={(e) => setShippingCharges(e.target.value)}
            />
          </Grid>
        </Grid>
        <Divider sx={{ marginY: 2 }} />
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Typography>
              <strong>Total Value (Without GST):</strong> Rs {totalValueWithoutGst}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography>
              <strong>Total GST Value:</strong> Rs {totalGstValue}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography>
              <strong>Total Invoice Value:</strong> Rs {totalInvoiceValue}
            </Typography>
          </Grid>
        </Grid>
      </Box>

      {/* Action Buttons */}
      <Box sx={{ marginTop: 4, display: "flex", justifyContent: "flex-end", gap: 2 }}>
        <Button
          variant="outlined"
          color="secondary"
          onClick={() => {
            if (window.confirm("Are you sure you want to reset the form?")) {
              fetchInvoiceDetails();
            }
          }}
        >
          Reset
        </Button>
        <Button variant="contained" color="primary" onClick={handleSubmit}>
          Save Changes
        </Button>
      </Box>
    </Paper>
  );
};

export default AdminEditSellerInvoice;
